import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';

const MIN_PASSWORD_LENGTH = 8;

export default function AcceptInvite() {
  const [token, setToken]     = useState('');
  const [invite, setInvite]   = useState(null);   // { email, account_name, has_login }
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();

  // ?it= in the query string, not a URL fragment — mail clients open links in
  // in-app browsers that drop fragments, which is what broke the first
  // password reset flow.
  useEffect(() => {
    const it = new URLSearchParams(window.location.search).get('it');
    if (!it) { setChecking(false); return; }
    setToken(it);
    axios.get('/api/invites/lookup', { params: { it } })
      .then(r => setInvite(r.data))
      .catch(err => setError(err.response?.data?.error || 'That invitation link is not valid.'))
      .finally(() => setChecking(false));
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    // An existing user is signing in to accept, so their password is whatever
    // it already is — only a brand new login has to meet the length rule or
    // be typed twice.
    if (!invite.has_login) {
      if (password.length < MIN_PASSWORD_LENGTH) {
        setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
        return;
      }
      if (password !== confirm) {
        setError('Those passwords don’t match');
        return;
      }
    }

    setLoading(true);
    try {
      const { data } = await axios.post('/api/invites/accept', { token, password });
      login(data.token, data.account);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const marketingUrl = process.env.REACT_APP_MARKETING_URL || 'https://velaleads.ca';

  return (
    <div style={pageStyle}>
      <a href={marketingUrl} style={logoStyle} aria-label="Vela home">
        <img src="/logos/vela-wordmark.png" alt="" style={{ height: 22, width: 'auto', display: 'block' }} />
      </a>

      <div style={cardStyle}>
        {checking ? (
          <p style={subheadStyle}>Checking your invitation&hellip;</p>
        ) : !invite ? (
          <>
            <h1 style={headingStyle}>Invitation</h1>
            <p style={subheadStyle}>
              {error || 'This link didn’t come through complete. Ask for a new one and it should work.'}
            </p>
            <p style={switchModeStyle}>
              <span onClick={() => navigate('/login')} style={switchModeLinkStyle}>Go to sign in</span>
            </p>
          </>
        ) : (
          <>
            <h1 style={headingStyle}>Join {invite.account_name}</h1>
            <p style={subheadStyle}>
              {invite.has_login
                ? `Sign in as ${invite.email} to accept.`
                : `Set a password for ${invite.email} and you're in.`}
            </p>

            {error && <div style={errorStyle}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <label style={labelStyle}>
                {invite.has_login ? 'Your Vela password' : 'Choose a password'}
              </label>
              <input style={inputStyle} type="password" required autoFocus
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder={invite.has_login ? '••••••••' : 'At least 8 characters'} />

              {!invite.has_login && (
                <>
                  <label style={labelStyle}>Confirm password</label>
                  <input style={inputStyle} type="password" required
                    value={confirm} onChange={e => setConfirm(e.target.value)}
                    placeholder="••••••••" />
                </>
              )}

              <button type="submit" disabled={loading}
                style={{ ...buttonStyle, opacity: loading ? 0.7 : 1 }}>
                <span style={buttonTextStyle}>
                  {loading ? 'Please wait…' : 'Join the team'}
                </span>
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
  justifyContent: 'center', gap: 28, background: 'var(--vela-bg)', padding: '40px 20px',
};
const logoStyle = { display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' };
const cardStyle = {
  background: 'var(--vela-glass-bg)', backdropFilter: 'blur(24px) saturate(160%)',
  WebkitBackdropFilter: 'blur(24px) saturate(160%)', border: '1px solid var(--vela-glass-border)',
  borderRadius: 20, padding: '40px 36px', width: 380, maxWidth: '100%',
};
const headingStyle = {
  fontFamily: "'Inter', -apple-system, sans-serif", fontWeight: 600, fontSize: 22,
  color: 'var(--vela-text)', marginBottom: 6, lineHeight: 1.15, textAlign: 'center',
};
const subheadStyle = { color: 'var(--vela-text-mid)', marginBottom: 28, fontSize: 14, textAlign: 'center' };
const errorStyle = {
  background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)',
  color: '#fca5a5', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13,
};
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 5, color: 'var(--vela-text-mid)' };
const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid var(--vela-glass-border-bright)',
  background: 'var(--vela-glass-bg)', color: 'var(--vela-text)',
  borderRadius: 8, fontSize: 14, marginBottom: 14, outline: 'none', fontFamily: 'inherit',
};
const buttonStyle = {
  width: '100%', position: 'relative', overflow: 'hidden', border: 'none',
  borderRadius: 100, padding: '13px', fontSize: 15, fontWeight: 600,
  cursor: 'pointer', marginTop: 8, color: 'var(--primary-contrast)',
  backgroundImage: 'var(--vela-opal-grad)',
};
const buttonTextStyle = { position: 'relative', zIndex: 1 };
const switchModeStyle = { textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--vela-text-mid)' };
const switchModeLinkStyle = { color: 'var(--vela-text)', cursor: 'pointer', fontWeight: 500 };
